import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext, AppRole, AuthContextType, LGPDConsent, Profile, UserRole } from './auth-context-base';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { resolvePrimaryRole } from '@/permissions';
import { AuthSessionSchema } from '@/lib/schemas/auth.schema';
import {
  startSessionMonitor,
  stopSessionMonitor,
  signOutDueToTimeout,
  renewActivityNow,
  clearSessionPolicy,
  type ExpireReason,
} from '@/lib/sessionTimeout';

/**
 * SEGURANÇA: Função para limpar todo o cache do sistema
 * Usada no logout e na troca de usuário para evitar vazamento de dados
 */
const clearAllUserCache = () => {
  // Limpar localStorage (exceto preferências de UI)
  const keysToKeep = ['theme', 'language', 'i18nextLng'];
  const allKeys = Object.keys(localStorage);
  allKeys.forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  // Limpar sessionStorage completamente
  sessionStorage.clear();

  console.debug('[AuthContext] Cache de usuário limpo');
};

const PROFILE_CACHE_TTL = 10 * 60 * 1000; // 10 minutes - increased to prevent reload from repeated auth events
const FETCH_PROFILE_DEBOUNCE_MS = 500; // Increased from 300ms to 500ms
const LOADING_TIMEOUT_MS = 15000; // Increased from 10s to 15s
const VISIBILITY_CHANGE_DEBOUNCE_MS = 1000; // New: debounce for visibility changes
// Timeouts de sessão agora são definidos em src/lib/sessionTimeout.ts
// (variáveis SESSION_LIMITS.ephemeral vs remembered).

const debugAuth = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.debug(`[AuthContext:${timestamp}]`, ...args);
  }
};

const normalizeRole = (role: string | null | undefined): AppRole | null => {
  if (!role) return null;

  switch (role) {
    case 'admin':
    case 'partner':
    case 'assessor':
    case 'investidor':
    case 'mfb':
    case 'mfa':
    case 'incorporador':
    case 'diligencias_manager':
      return role;
    case 'associado':
      return 'assessor';
    case 'investor':
      return 'investidor';
    default:
      return null;
  }
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRoleOverride, setActiveRoleOverride] = useState<AppRole | null>(
    () => sessionStorage.getItem('activeRoleOverride') as AppRole | null,
  );
  const profileRef = useRef<Profile | null>(null);
  const userRolesRef = useRef<UserRole[]>([]);
  const profileFetchCacheRef = useRef<{ userId: string | null; timestamp: number }>({
    userId: null,
    timestamp: 0,
  });
  const fetchProfileTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);
  const lastVisibilityChangeRef = useRef<number>(0);
  const previousUserIdRef = useRef<string | null>(null); // SEGURANÇA: Rastrear usuário anterior
  const { toast } = useToast();
  const queryClient = useQueryClient(); // SEGURANÇA: Para limpar cache do React Query

  const metadataRole = normalizeRole(
    ((user?.user_metadata ?? {}) as { role?: string }).role,
  );

  const emailRoleMap: Record<string, AppRole> = {
    'admin@bravaforte.com': 'admin',
    'parceiro@bravaforte.com': 'partner',
    'assessor@bravaforte.com': 'assessor',
    'investidor@bravaforte.com': 'investidor',
  };

  const fallbackRole = metadataRole ?? (user ? emailRoleMap[user.email ?? ''] : undefined);

  const primaryRole = useMemo<AppRole | null>(() => {
    // Coleta todos os roles disponíveis (user_roles, profile.role, fallback)
    const allRoles: AppRole[] = [];

    for (const ur of userRoles) {
      if (ur.role) allRoles.push(ur.role);
    }

    if (profile?.role && !allRoles.includes(profile.role)) {
      allRoles.push(profile.role);
    }

    if (fallbackRole && !allRoles.includes(fallbackRole)) {
      allRoles.push(fallbackRole);
    }

    // Usa resolução por prioridade (admin > partner > mfb > mfa > assessor > incorporador > investidor),
    // a menos que o usuário tenha escolhido explicitamente um papel na Tela 3 do login por PIN.
    return activeRoleOverride ?? resolvePrimaryRole(allRoles) ?? null;
  }, [userRoles, profile, fallbackRole, activeRoleOverride]);

  const setActiveRole = useCallback((role: AppRole) => {
    sessionStorage.setItem('activeRoleOverride', role);
    setActiveRoleOverride(role);
  }, []);

  // Utility functions for timeout and debounce control
  const clearAllTimeouts = useCallback(() => {
    if (fetchProfileTimeoutRef.current) {
      clearTimeout(fetchProfileTimeoutRef.current);
      fetchProfileTimeoutRef.current = null;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (visibilityTimeoutRef.current) {
      clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = null;
    }
  }, []);

  const setLoadingWithTimeout = useCallback((isLoading: boolean) => {
    debugAuth('setLoadingWithTimeout', {
      isLoading,
      isFetching: isFetchingRef.current,
      pageHidden: document.hidden
    });

    setLoading(isLoading);

    if (isLoading) {
      // Set timeout to reset loading if it takes too long
      clearAllTimeouts();
      loadingTimeoutRef.current = setTimeout(() => {
        debugAuth('loading timeout reached, resetting to false', {
          timeoutMs: LOADING_TIMEOUT_MS,
          wasStillFetching: isFetchingRef.current
        });
        setLoading(false);
        isFetchingRef.current = false;
      }, LOADING_TIMEOUT_MS);
    } else {
      clearAllTimeouts();
    }
  }, [clearAllTimeouts]);

  const hasRole = (role: AppRole) =>
    userRoles.some(userRole => userRole.role === role) ||
    profile?.role === role ||
    fallbackRole === role;

  const isAdmin = hasRole('admin');
  const isPartner = hasRole('partner');
  const isAssessor = hasRole('assessor');
  const isInvestor = hasRole('investidor');
  const isMFB = hasRole('mfb');
  const isMFA = hasRole('mfa');
  const isIncorporador = hasRole('incorporador');
  const isDiligenciasManager = hasRole('diligencias_manager');

  const fetchProfile = useCallback(async (userId: string, forceRefresh = false) => {
    // Clear any existing debounce timeout
    if (fetchProfileTimeoutRef.current) {
      clearTimeout(fetchProfileTimeoutRef.current);
      fetchProfileTimeoutRef.current = null;
    }

    // Skip fetch if page is not visible and not forcing refresh
    // Check document.hidden directly instead of using state to avoid re-creating this callback
    const currentlyVisible = !document.hidden;
    if (!currentlyVisible && !forceRefresh) {
      debugAuth('fetchProfile:skipPageNotVisible', { userId });
      return;
    }

    // Check if already fetching to prevent race conditions
    if (isFetchingRef.current) {
      debugAuth('fetchProfile:skipAlreadyFetching', { userId });
      return;
    }

    const cachedProfile = profileFetchCacheRef.current;
    const now = Date.now();

    // Prevent excessive fetches when page visibility changes rapidly
    if (!forceRefresh && (now - lastVisibilityChangeRef.current) < VISIBILITY_CHANGE_DEBOUNCE_MS) {
      debugAuth('fetchProfile:skipVisibilityDebounce', { userId, timeSinceLastChange: now - lastVisibilityChangeRef.current });
      return;
    }
    const hasCachedProfile =
      profileRef.current?.user_id === userId &&
      (userRolesRef.current.length > 0 || Boolean(profileRef.current?.role));

    // Check cache first
    if (hasCachedProfile && cachedProfile.userId === userId && now - cachedProfile.timestamp < PROFILE_CACHE_TTL) {
      debugAuth('fetchProfile:skipCached', { userId });
      return;
    }

    // Debounce the actual fetch
    fetchProfileTimeoutRef.current = setTimeout(async () => {
      if (isFetchingRef.current) {
        debugAuth('fetchProfile:skipRaceCondition', { userId });
        return;
      }

      isFetchingRef.current = true;
      setLoadingWithTimeout(true);
      debugAuth('fetchProfile:start', { userId });

      // Update visibility change timestamp to prevent rapid re-fetches
      lastVisibilityChangeRef.current = now;

      profileFetchCacheRef.current = { userId, timestamp: now };

      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (profileError) {
          debugAuth('fetchProfile:profileError', profileError);
        }

        const { data: initialRolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);

        let rolesData = initialRolesData;

        if (rolesError) {
          debugAuth('fetchProfile:rolesError', rolesError);
        }

        debugAuth('fetchProfile:data', { profileData, rolesData });

        let effectiveProfileData = profileData;

        // Fallback: se o perfil não existe (signup com email confirmation),
        // criamos o perfil automaticamente usando os metadados do auth.users
        if (!effectiveProfileData && userId) {
          debugAuth('fetchProfile:noProfile, creating fallback profile', { userId });

          const { data: { user: authUser }, error: authUserError } = await supabase.auth.getUser();

          // Só prosseguir se temos um usuário válido autenticado
          if (authUserError || !authUser) {
            debugAuth('fetchProfile:fallbackSkipped, no valid auth user', { authUserError });
          } else {

          const meta = authUser.user_metadata ?? {};

          const fallbackProfileRole = normalizeRole(meta.role) ?? 'investidor';

          const { data: createdProfile, error: createError } = await supabase
            .from('profiles')
            .upsert({
              user_id: userId,
              email: authUser?.email ?? '',
              first_name: meta.first_name ?? '',
              last_name: meta.last_name ?? '',
              cpf: meta.cpf ?? null,
              phone: meta.phone ?? null,
              role: fallbackProfileRole,
              status: 'active',
            }, { onConflict: 'user_id' })
            .select('*')
            .single();

          if (createError) {
            debugAuth('fetchProfile:fallbackCreateError', createError);
          } else {
            debugAuth('fetchProfile:fallbackCreated', { createdProfile });
            effectiveProfileData = createdProfile;
          }

          // Também garantir que user_roles existe
          if (!rolesData?.length) {
            const { error: roleError } = await supabase
              .from('user_roles')
              .upsert({ user_id: userId, role: fallbackProfileRole }, { onConflict: 'user_id,role' });

            if (roleError) {
              debugAuth('fetchProfile:fallbackRoleError', roleError);
            } else {
              // Refetch roles
              const { data: freshRoles } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', userId);
              if (freshRoles) {
                rolesData = freshRoles;
              }
            }
          }

          } // end else (valid auth user)
        }

        const normalizedProfileRole = normalizeRole(effectiveProfileData?.role);

        if (effectiveProfileData) {
          const newProfile = {
            ...effectiveProfileData,
            assessor_id: effectiveProfileData.assessor_id ?? null,
            partner_id: effectiveProfileData.partner_id ?? null,
            incorporadora_id: effectiveProfileData.incorporadora_id ?? null,
            role: normalizedProfileRole,
            diligencia_especialidade: effectiveProfileData.diligencia_especialidade ?? null,
          } as Profile;

          setProfile(newProfile);
          profileRef.current = newProfile;

        } else {
          setProfile(null);
          profileRef.current = null;
        }

        const normalizedRoles = (rolesData ?? [])
          .map(({ role }) => normalizeRole(role))
          .filter((role): role is AppRole => Boolean(role));

        debugAuth('fetchProfile:normalizedRoles', { normalizedRoles, normalizedProfileRole });

        const newUserRoles = normalizedRoles.length > 0
          ? normalizedRoles.map(role => ({ role }))
          : normalizedProfileRole
            ? [{ role: normalizedProfileRole }]
            : [];

        setUserRoles(newUserRoles);
        userRolesRef.current = newUserRoles;

        profileFetchCacheRef.current = { userId, timestamp: Date.now() };
      } catch (error) {
        debugAuth('fetchProfile:error', error);
        setProfile(null);
        setUserRoles([]);
        profileRef.current = null;
        userRolesRef.current = [];
        profileFetchCacheRef.current = { userId: null, timestamp: 0 };
      } finally {
        isFetchingRef.current = false;
        debugAuth('fetchProfile:complete', { userId });
        setLoadingWithTimeout(false);
      }
    }, FETCH_PROFILE_DEBOUNCE_MS);
  }, [setLoadingWithTimeout]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      debugAuth('onAuthStateChange', { event, sessionEmail: session?.user?.email });

      const currentUserId = session?.user?.id ?? null;
      const previousUserId = previousUserIdRef.current;

      // SEGURANÇA: Detectar troca de usuário
      const isUserChange = previousUserId !== null &&
                          currentUserId !== null &&
                          previousUserId !== currentUserId;

      if (isUserChange) {
        debugAuth('onAuthStateChange:USER_CHANGE detected', {
          previousUserId,
          currentUserId,
          event
        });

        // CRÍTICO: Limpar TODO o cache ao trocar de usuário
        queryClient.clear();
        clearAllUserCache();
      }

      // Atualizar referência do usuário anterior
      previousUserIdRef.current = currentUserId;

      setSession(session);
      setUser(session?.user ?? null);

      if (!session?.user) {
        setProfile(null);
        setUserRoles([]);
        setLoading(false);
        profileFetchCacheRef.current = { userId: null, timestamp: 0 };
      } else {
        // Avoid Supabase async calls directly inside onAuthStateChange.
        // Work around known supabase-js deadlock by deferring async work.
        window.setTimeout(() => {
          void (async () => {
            // Only force refresh on specific events that require fresh data
            // SIGNED_IN events happen repeatedly (every ~30-60s) due to token refresh
            // but don't actually need profile refetch - use cache instead
            // USER_UPDATED fires for both password and email changes;
            // only force refresh when email actually changed (password change doesn't affect profile)
            const isEmailChange = event === 'USER_UPDATED' &&
              session.user.email !== profileRef.current?.email;
            const eventsRequiringRefresh = ['INITIAL_SESSION', 'PASSWORD_RECOVERY'];
            const shouldForceRefresh = eventsRequiringRefresh.includes(event) || isEmailChange || isUserChange;

            debugAuth('onAuthStateChange:fetchProfile', {
              event,
              shouldForceRefresh,
              isUserChange,
              reason: shouldForceRefresh
                ? `${event} requires fresh data`
                : `${event} using cached profile to prevent reload`
            });

            // Sincronizar email da tabela profiles quando o usuário confirma alteração de email
            if (event === 'USER_UPDATED' && session.user.email) {
              debugAuth('onAuthStateChange:USER_UPDATED - syncing email to profiles', {
                userId: session.user.id,
                newEmail: session.user.email
              });

              try {
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({
                    email: session.user.email,
                    updated_at: new Date().toISOString()
                  })
                  .eq('user_id', session.user.id);

                if (updateError) {
                  debugAuth('onAuthStateChange:USER_UPDATED - email sync error', updateError);
                  console.error('Erro ao sincronizar email no perfil:', updateError);
                } else {
                  debugAuth('onAuthStateChange:USER_UPDATED - email synced successfully');
                  toast({
                    title: 'Email atualizado!',
                    description: 'Seu email foi alterado com sucesso.',
                  });
                }
              } catch (syncError) {
                debugAuth('onAuthStateChange:USER_UPDATED - sync exception', syncError);
                console.error('Exceção ao sincronizar email:', syncError);
              }
            }

            // SEGURANÇA: Ao fazer login, invalidar queries para forçar refetch
            if (event === 'SIGNED_IN' && !previousUserId) {
              queryClient.invalidateQueries();
            }

            await fetchProfile(session.user.id, shouldForceRefresh);
          })();
        }, 0);
      }

      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        debugAuth(`${event} - using cached profile to prevent reload effect`);
      } else if (event === 'SIGNED_OUT') {
        debugAuth('onAuthStateChange:SIGNED_OUT cleanup');

        // SEGURANÇA: Limpar TODO o cache no logout
        queryClient.clear();
        clearAllUserCache();

        clearAllTimeouts();
        isFetchingRef.current = false;
        setProfile(null);
        setUserRoles([]);
        profileRef.current = null;
        userRolesRef.current = [];
        profileFetchCacheRef.current = { userId: null, timestamp: 0 };
        previousUserIdRef.current = null;
        // sessionStorage já foi limpo por clearAllUserCache() acima; o estado
        // React precisa ser resetado à parte para não herdar a escolha de
        // papel de uma sessão anterior no próximo login por PIN.
        setActiveRoleOverride(null);
      }
    });

    /**
     * Phase 1 D-20: Zod gate para sessão rehidratada do localStorage.
     * Retorna true se a sessão tem shape válido (ou é null/undefined — caso
     * "sem sessão" não dispara gate). Retorna false se shape inválido — neste
     * caso JÁ executou cleanup ordenado (clearAllUserCache → signOut local →
     * redirect /auth) e o caller deve abortar o fluxo de inicialização.
     *
     * Pitfall §4 (loop avoidance): clearAllUserCache PRIMEIRO remove a chave
     * sb-*-auth-token do localStorage; só então signOut + redirect. Se a
     * ordem inverter, page reload re-fire o mesmo Zod-fail.
     */
    const validateRehydratedSession = async (
      session: Session | null,
    ): Promise<boolean> => {
      if (!session) return true;
      const validation = AuthSessionSchema.safeParse(session);
      if (validation.success) return true;

      // PII-safe logging: apenas {path, code} — sem message, sem input value (D-06).
      console.warn('[auth] localStorage session shape invalid', {
        issues: validation.error.issues.map(i => ({
          path: i.path.join('.'),
          code: i.code,
        })),
      });

      // Ordem é crítica (Pitfall §4 + WR-03 fix):
      // 1) clearAllUserCache — remove sb-*-auth-token + outros não-whitelist
      clearAllUserCache();
      // 2) supabase.auth.signOut local — invalida o estado interno do SDK
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutErr) {
        debugAuth('initializeAuth:zodGate signOut error', signOutErr);
      }
      // 3) Limpar refs e estabilizar UI ANTES do redirect (WR-03):
      //    setting window.location.href dispara navegação assíncrona, mas o JS
      //    continua executando síncrono — se setLoading(false) rodasse depois,
      //    haveria um React commit com loading=false + user=null durante o
      //    intervalo até unload, flashando UI não-autenticada para consumidores
      //    que renderizam baseado em (loading === false && user === null).
      profileFetchCacheRef.current = { userId: null, timestamp: 0 };
      setLoading(false);
      // 4) Redirect — última ação. Após este ponto qualquer mutação de state
      //    é desperdiçada (página em mid-unload).
      if (typeof window !== 'undefined') {
        window.location.href = '/auth';
      }
      return false;
    };

    // Initial session check with retry logic
    const initializeAuth = async () => {
      debugAuth('initializeAuth:start');

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        debugAuth('initializeAuth:getSession', { hasSession: Boolean(session), error });

        if (error) {
          debugAuth('initializeAuth:getSession error, attempting refresh', error);
          await supabase.auth.refreshSession();
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.getSession();
          debugAuth('initializeAuth:refresh result', { hasSession: Boolean(refreshedSession), refreshError });

          // Phase 1 D-20: Zod gate antes de propagar sessão para state.
          if (!(await validateRehydratedSession(refreshedSession))) return;

          setSession(refreshedSession);
          setUser(refreshedSession?.user ?? null);

          if (refreshedSession?.user) {
            await fetchProfile(refreshedSession.user.id, true);
          } else {
            setLoading(false);
            profileFetchCacheRef.current = { userId: null, timestamp: 0 };
          }
        } else {
          // Phase 1 D-20: Zod gate antes de propagar sessão para state.
          if (!(await validateRehydratedSession(session))) return;

          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            await fetchProfile(session.user.id, true);
          } else {
            setLoading(false);
            profileFetchCacheRef.current = { userId: null, timestamp: 0 };
          }
        }
      } catch (initError) {
        debugAuth('initializeAuth:error', initError);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
      clearAllTimeouts();
      isFetchingRef.current = false;
    };
  }, [clearAllTimeouts, fetchProfile, queryClient, toast]);

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    cpf: string,
    phone: string,
    lgpdConsent?: LGPDConsent,
  ) => {
    debugAuth('signUp:attempt', { email, firstName, lastName });

    const redirectUrl = `${window.location.origin}/`;

    const { data: authData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          cpf,
          phone,
        },
      },
    });

    if (error) {
      debugAuth('signUp:error', { email, error });
      toast({
        title: 'Erro no cadastro',
        description: error.message,
        variant: 'destructive',
      });
      return { error };
    }

    if (authData.user) {
      try {
        // Trigger handle_new_user já cria o perfil via auth.users insert.
        // Usamos upsert para garantir os dados corretos e status active.
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            user_id: authData.user.id,
            email,
            first_name: firstName,
            last_name: lastName,
            cpf,
            phone,
            role: 'investidor',
            status: 'active',
          }, { onConflict: 'user_id' });

        if (profileError) {
          debugAuth('signUp:profileError', profileError);
          console.error('Error creating profile:', profileError);
        }

        if (lgpdConsent) {
          debugAuth('signUp:lgpdConsent', { userId: authData.user.id, lgpdConsent });
        }

        debugAuth('signUp:success', { email, userId: authData.user.id });
      } catch (profileException) {
        console.error('Error during profile creation:', profileException);
      }

      toast({
        title: 'Cadastro realizado!',
        description: 'Sua conta foi criada com sucesso. Você já pode acessar a plataforma.',
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    debugAuth('signIn:attempt', { email });

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      debugAuth('signIn:error', { email, error });
      toast({
        title: 'Erro no login',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      debugAuth('signIn:success', { email, sessionUser: data?.user?.email });
    }

    return { error };
  };

  const signInWithPinToken = async (email: string, token: string) => {
    debugAuth('signInWithPinToken:attempt', { email });

    // Troca o token de magic-link (emitido pela Edge Function pin-login) por
    // uma sessão real. O onAuthStateChange padrão (SIGNED_IN → fetchProfile)
    // cuida do resto exatamente como no login por senha — nenhum caminho
    // especial de RLS/perfil é necessário.
    // O valor recebido é o hashed_token de admin.generateLink(), então vai em
    // token_hash — não em token (que é reservado para o código OTP de 6 dígitos).
    const { error } = await supabase.auth.verifyOtp({ token_hash: token, type: 'magiclink' });

    if (error) {
      debugAuth('signInWithPinToken:error', { email, error });
      toast({
        title: 'Erro no login',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      debugAuth('signInWithPinToken:success', { email });
    }

    return { error };
  };

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    debugAuth('refreshProfile:start', { userId: user.id });

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileData) {
        const normalizedProfileRole = normalizeRole(profileData.role);
        const newProfile = {
          ...profileData,
          assessor_id: profileData.assessor_id ?? null,
          partner_id: profileData.partner_id ?? null,
          incorporadora_id: profileData.incorporadora_id ?? null,
          role: normalizedProfileRole,
          diligencia_especialidade: profileData.diligencia_especialidade ?? null,
        } as Profile;

        setProfile(newProfile);
        profileRef.current = newProfile;
        profileFetchCacheRef.current = { userId: user.id, timestamp: Date.now() };
        debugAuth('refreshProfile:success', { incorporadora_id: newProfile.incorporadora_id });
      }
    } catch (error) {
      debugAuth('refreshProfile:error', error);
    }
  }, [user?.id]);

  const updateProfileAvatar = useCallback((avatarUrl: string | null) => {
    debugAuth('updateProfileAvatar', { avatarUrl });

    setProfile(prev => {
      if (!prev) return prev;
      const updated = { ...prev, avatar_url: avatarUrl };
      profileRef.current = updated;
      return updated;
    });
  }, []);

  const signOut = useCallback(async () => {
    debugAuth('signOut:attempt', { userEmail: user?.email });

    const clearLocalSession = () => {
      // SEGURANÇA: Limpar React Query cache PRIMEIRO
      try {
        queryClient.clear();
        debugAuth('signOut:queryClientCleared');
      } catch (qcError) {
        debugAuth('signOut:queryClientClearError', qcError);
      }

      // Limpa política de sessão (flags do banking-style timeout)
      clearSessionPolicy();
      stopSessionMonitor();

      // SEGURANÇA: Limpar TODO o cache do usuário (localStorage + sessionStorage)
      clearAllUserCache();

      try {
        const storageKey = (supabase.auth as { storageKey?: string }).storageKey;
        debugAuth('signOut:storageKey', { storageKey });
        if (storageKey) {
          localStorage.removeItem(storageKey);
          sessionStorage.removeItem(storageKey);
        }
      } catch (storageError) {
        debugAuth('signOut:storageCleanupError', storageError);
        console.warn('Nao foi possivel limpar o storage da sessao:', storageError);
      }

      setSession(null);
      setUser(null);
      setProfile(null);
      setUserRoles([]);
      profileFetchCacheRef.current = { userId: null, timestamp: 0 };
      previousUserIdRef.current = null;
    };

    clearLocalSession();

    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        debugAuth('signOut:error', error);
        toast({
          title: 'Erro ao sair',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      debugAuth('signOut:complete');
      return { error: null };
    } catch (signOutError) {
      const formattedError = signOutError instanceof Error
        ? signOutError
        : new Error('Falha ao encerrar a sessao.');

      debugAuth('signOut:exception', formattedError);
      toast({
        title: 'Erro ao sair',
        description: formattedError.message,
        variant: 'destructive',
      });
      return { error: formattedError };
    }
  }, [user?.email, queryClient, toast]);

  // =============================================================================
  // INATIVIDADE E RE-AUTENTICAÇÃO (comportamento de app bancário)
  // =============================================================================

  // Session timeout (banking-style): idle + absolute, com valores diferentes
  // para "lembrar login" (7 dias / 2h idle) vs efêmero (8h / 30min idle).
  // Persistido em localStorage. signOut scope:'local' preserva push subscription.
  useEffect(() => {
    const enabled = Boolean(session && user && !loading);
    if (!enabled) {
      stopSessionMonitor();
      return;
    }

    const onExpire = async (reason: ExpireReason) => {
      debugAuth('sessionTimeout:expired', { reason, userEmail: user?.email });
      const description = reason === 'absolute'
        ? 'Sua sessão atingiu o tempo máximo. Faça login novamente.'
        : 'Sua sessão expirou por inatividade. Faça login novamente.';
      toast({
        title: 'Sessão expirada',
        description,
        variant: 'default',
      });

      await signOutDueToTimeout();
      // signOut scope:'local' dispara onAuthStateChange SIGNED_OUT
      // que limpa o estado. Redireciona para /auth.
      if (typeof window !== 'undefined') {
        window.location.href = '/auth';
      }
    };

    const onWarn = (msRemaining: number) => {
      const mins = Math.max(1, Math.round(msRemaining / 60000));
      toast({
        title: 'Sessão expirando',
        description: `Sua sessão expira em ~${mins}min. Clique em qualquer lugar para manter conectado.`,
        duration: 10_000,
      });
    };

    startSessionMonitor({ onExpire, onWarn });
    return () => stopSessionMonitor();
  }, [session, user, loading, toast]);

  // Exposto para permitir que o user clique em "continuar" no toast de aviso
  const renewSession = useCallback(() => renewActivityNow(), []);
  void renewSession;

  useEffect(() => {
    debugAuth('state snapshot', {
      loading,
      userEmail: user?.email,
      sessionId: session?.access_token?.slice(0, 12),
      primaryRole,
      profileRole: profile?.role,
      userRoles,
    });
  }, [loading, user, session, primaryRole, profile, userRoles]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    userRolesRef.current = userRoles;
  }, [userRoles]);

  const value = {
    user,
    session,
    profile,
    userRoles,
    isAdmin,
    isPartner,
    isAssessor,
    isInvestor,
    isMFB,
    isMFA,
    isIncorporador,
    isDiligenciasManager,
    primaryRole,
    activeRoleOverride,
    setActiveRole,
    loading,
    signUp,
    signIn,
    signInWithPinToken,
    signOut,
    updateProfileAvatar,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};



